// src/components/AddLocalidadForm.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Form, ListGroup } from "react-bootstrap";
import "animate.css";
import "../css/addlocalidadform.css";

import {
  postLocalidad as addLocalidad,
  getLocalidades,
} from "../helpers/rutaLocalidades";
import { getProvincias } from "../helpers/rutaProvincias";
import { getDeptos } from "../helpers/rutaDeptos";

/* ---------------- utils ---------------- */
const clean = (v) => String(v ?? "").trim();

const toIntOrZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeProvincias = (r) => {
  const arr = Array.isArray(r?.provincias) ? r.provincias : Array.isArray(r) ? r : [];

  return arr.map((p) => ({
    ...p,
    codprov:
      Number(
        p?.codprov ??
          p?.codprovi ??
          p?.codprovincia ??
          p?.codigo ??
          p?.cod ??
          p?.numero ??
          0
      ) || 0,
  }));
};

const normalizeDeptos = (r) => {
  const arr = Array.isArray(r?.deptos) ? r.deptos : Array.isArray(r) ? r : [];

  return arr.map((d) => ({
    ...d,
    coddept:
      Number(
        d?.coddept ??
          d?.coddepto ??
          d?.codigo ??
          d?.cod ??
          d?.numero ??
          0
      ) || 0,
  }));
};

const normalizeLocalidades = (r) => {
  const arr = Array.isArray(r?.localidades) ? r.localidades : Array.isArray(r) ? r : [];

  return arr.map((l) => ({
    ...l,
    codloca:
      Number(
        l?.codloca ??
          l?.codlocal ??
          l?.codlocalidad ??
          l?.codigo ??
          l?.cod ??
          l?.numero ??
          0
      ) || 0,
  }));
};

/* ---------------- helpers Provincia ---------------- */
const getProvinciaDocId = (p) =>
  clean(p?._id ?? p?.id ?? p?.provinciaId ?? p?.Provincia ?? p?.value ?? "");

const getCodProv = (p) => {
  const raw =
    p?.codprov ??
    p?.codprovi ??
    p?.codprovincia ??
    p?.codigo ??
    p?.cod ??
    p?.numero ??
    "";

  const s = String(raw ?? "").trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const getDescProv = (p) =>
  clean(p?.provincia) ||
  clean(p?.descripcion) ||
  clean(p?.nombre) ||
  clean(p?.label) ||
  "";

/* ---------------- helpers Depto ---------------- */
const getDeptoDocId = (d) =>
  clean(d?._id ?? d?.id ?? d?.deptoId ?? d?.Depto ?? d?.value ?? "");

const getCodDept = (d) => {
  const raw = d?.coddept ?? d?.coddepto ?? d?.codigo ?? d?.cod ?? d?.numero ?? "";
  const s = String(raw ?? "").trim();
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const getDescDept = (d) =>
  clean(d?.depto) ||
  clean(d?.descripcion) ||
  clean(d?.nombre) ||
  clean(d?.label) ||
  "";

/* ---------------- initial ---------------- */
const initial = {
  codloca: "",
  localidad: "",
  codpostal: "",
  codpais: "1",

  codprov: "",
  provincia: "",
  provinciaId: "",

  coddept: "",
  depto: "",
  deptoId: "",
};

export default function AddLocalidadForm({ setShow }) {
  const [formValues, setFormValues] = useState(initial);
  const [showSuccess, setShowSuccess] = useState(false);

  const [ui, setUi] = useState({
    provinciaDesc: "",
    deptoDesc: "",
  });

  const [provinciasState, setProvinciasState] = useState({
    data: [],
    loading: true,
  });

  const [deptosState, setDeptosState] = useState({
    data: [],
    loading: true,
  });

  const [localidadesState, setLocalidadesState] = useState({
    data: [],
    loading: true,
  });

  const setField = useCallback((name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const getNextCodLoc = useCallback((arr = []) => {
    const max = arr.reduce((acc, item) => {
      const n = Number(item?.codloca ?? 0) || 0;
      return n > acc ? n : acc;
    }, 0);

    return String(max + 1);
  }, []);

  /* =========================================================
     CARGA INICIAL
  ========================================================= */
  useEffect(() => {
    getProvincias()
      .then((r) => {
        const data = normalizeProvincias(r);
        setProvinciasState({
          data,
          loading: false,
        });
      })
      .catch(() =>
        setProvinciasState({
          data: [],
          loading: false,
        })
      );
  }, []);

  useEffect(() => {
    getDeptos()
      .then((r) => {
        const data = normalizeDeptos(r);
        setDeptosState({
          data,
          loading: false,
        });
      })
      .catch(() =>
        setDeptosState({
          data: [],
          loading: false,
        })
      );
  }, []);

  useEffect(() => {
    getLocalidades()
      .then((r) => {
        const data = normalizeLocalidades(r);

        setLocalidadesState({
          data,
          loading: false,
        });

        setFormValues((prev) => ({
          ...prev,
          codloca: prev.codloca || getNextCodLoc(data),
        }));
      })
      .catch(() =>
        setLocalidadesState({
          data: [],
          loading: false,
        })
      );
  }, [getNextCodLoc]);

  /* =========================================================
     PROVINCIAS
  ========================================================= */
  const [showProvModal, setShowProvModal] = useState(false);
  const [qProv, setQProv] = useState("");
  const [provOpts, setProvOpts] = useState([]);
  const [selIdxProv, setSelIdxProv] = useState(0);
  const inputProvRef = useRef(null);

  const openProvModal = useCallback(() => {
    setShowProvModal(true);
    setQProv("");
    setProvOpts([]);
    setSelIdxProv(0);
    setTimeout(() => inputProvRef.current?.focus(), 50);
  }, []);

  const closeProvModal = useCallback(() => {
    setShowProvModal(false);
  }, []);

  const assignProvincia = useCallback(
    (prov) => {
      if (!prov) return;

      const id = getProvinciaDocId(prov);
      const codNum =
        getCodProv(prov) ||
        Number(provinciasState.data.find((x) => getProvinciaDocId(x) === id)?.codprov ?? 0) ||
        0;

      const desc = getDescProv(prov);

      setFormValues((prev) => ({
        ...prev,
        provinciaId: id,
        codprov: codNum > 0 ? String(codNum) : "",
        provincia: desc,
      }));

      setUi((prev) => ({
        ...prev,
        provinciaDesc: desc,
      }));
    },
    [provinciasState.data]
  );

  const fetchProvinciaByCod = useCallback(
    async (cod) => {
      const num = Number(cod || 0);

      if (!Number.isFinite(num) || num <= 0) {
        setUi((prev) => ({ ...prev, provinciaDesc: "" }));
        return;
      }

      const base = Array.isArray(provinciasState.data) ? provinciasState.data : [];

      const pick =
        base.find((x) => Number(getCodProv(x)) === num) ||
        base.find((x) => String(getCodProv(x)) === String(num)) ||
        null;

      if (pick) {
        assignProvincia(pick);
      } else {
        setUi((prev) => ({ ...prev, provinciaDesc: "No encontrada" }));
        setFormValues((prev) => ({
          ...prev,
          provinciaId: "",
          provincia: "",
        }));
      }
    },
    [provinciasState.data, assignProvincia]
  );

  const buscarProvincia = useCallback(
    async (rawOverride) => {
      const raw = clean(rawOverride ?? formValues.codprov);

      if (raw === "" || raw === "0") {
        openProvModal();
        return;
      }

      const cod = Number(raw);
      if (!Number.isFinite(cod) || cod <= 0) {
        openProvModal();
        return;
      }

      await fetchProvinciaByCod(cod);
    },
    [formValues.codprov, openProvModal, fetchProvinciaByCod]
  );

  useEffect(() => {
    if (!showProvModal) return;

    const q = qProv.trim().toLowerCase();
    const base = Array.isArray(provinciasState.data) ? provinciasState.data : [];

    if (!q) {
      setProvOpts(base.slice(0, 50));
      setSelIdxProv(0);
      return;
    }

    const filtered = base
      .filter((prov) => {
        const cod = String(getCodProv(prov));
        const desc = getDescProv(prov).toLowerCase();
        return cod.includes(q) || desc.includes(q);
      })
      .slice(0, 100);

    setProvOpts(filtered);
    setSelIdxProv(0);
  }, [qProv, showProvModal, provinciasState.data]);

  const seleccionarProvincia = useCallback(
    (prov) => {
      if (!prov) return;
      assignProvincia(prov);
      closeProvModal();
    },
    [assignProvincia, closeProvModal]
  );

  const onProvModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeProvModal();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelIdxProv((i) => Math.min(i + 1, Math.max(0, provOpts.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelIdxProv((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = provOpts[selIdxProv] || provOpts[0];
      if (pick) seleccionarProvincia(pick);
    }
  };

  /* =========================================================
     DEPTOS
  ========================================================= */
  const [showDeptoModal, setShowDeptoModal] = useState(false);
  const [qDepto, setQDepto] = useState("");
  const [deptoOpts, setDeptoOpts] = useState([]);
  const [selIdxDepto, setSelIdxDepto] = useState(0);
  const inputDeptoRef = useRef(null);

  const openDeptoModal = useCallback(() => {
    setShowDeptoModal(true);
    setQDepto("");
    setDeptoOpts([]);
    setSelIdxDepto(0);
    setTimeout(() => inputDeptoRef.current?.focus(), 50);
  }, []);

  const closeDeptoModal = useCallback(() => {
    setShowDeptoModal(false);
  }, []);

  const assignDepto = useCallback(
    (dep) => {
      if (!dep) return;

      const id = getDeptoDocId(dep);
      const codNum =
        getCodDept(dep) ||
        Number(deptosState.data.find((x) => getDeptoDocId(x) === id)?.coddept ?? 0) ||
        0;

      const desc = getDescDept(dep);

      setFormValues((prev) => ({
        ...prev,
        deptoId: id,
        coddept: codNum > 0 ? String(codNum) : "",
        depto: desc,
      }));

      setUi((prev) => ({
        ...prev,
        deptoDesc: desc,
      }));
    },
    [deptosState.data]
  );

  const fetchDeptoByCod = useCallback(
    async (cod) => {
      const num = Number(cod || 0);

      if (!Number.isFinite(num) || num <= 0) {
        setUi((prev) => ({ ...prev, deptoDesc: "" }));
        return;
      }

      const base = Array.isArray(deptosState.data) ? deptosState.data : [];

      const pick =
        base.find((x) => Number(getCodDept(x)) === num) ||
        base.find((x) => String(getCodDept(x)) === String(num)) ||
        null;

      if (pick) {
        assignDepto(pick);
      } else {
        setUi((prev) => ({ ...prev, deptoDesc: "No encontrado" }));
        setFormValues((prev) => ({
          ...prev,
          deptoId: "",
          depto: "",
        }));
      }
    },
    [deptosState.data, assignDepto]
  );

  const buscarDepto = useCallback(
    async (rawOverride) => {
      const raw = clean(rawOverride ?? formValues.coddept);

      if (raw === "" || raw === "0") {
        openDeptoModal();
        return;
      }

      const cod = Number(raw);
      if (!Number.isFinite(cod) || cod <= 0) {
        openDeptoModal();
        return;
      }

      await fetchDeptoByCod(cod);
    },
    [formValues.coddept, openDeptoModal, fetchDeptoByCod]
  );

  useEffect(() => {
    if (!showDeptoModal) return;

    const q = qDepto.trim().toLowerCase();
    const base = Array.isArray(deptosState.data) ? deptosState.data : [];

    if (!q) {
      setDeptoOpts(base.slice(0, 50));
      setSelIdxDepto(0);
      return;
    }

    const filtered = base
      .filter((dep) => {
        const cod = String(getCodDept(dep));
        const desc = getDescDept(dep).toLowerCase();
        return cod.includes(q) || desc.includes(q);
      })
      .slice(0, 100);

    setDeptoOpts(filtered);
    setSelIdxDepto(0);
  }, [qDepto, showDeptoModal, deptosState.data]);

  const seleccionarDepto = useCallback(
    (dep) => {
      if (!dep) return;
      assignDepto(dep);
      closeDeptoModal();
    },
    [assignDepto, closeDeptoModal]
  );

  const onDeptoModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeDeptoModal();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelIdxDepto((i) => Math.min(i + 1, Math.max(0, deptoOpts.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelIdxDepto((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = deptoOpts[selIdxDepto] || deptoOpts[0];
      if (pick) seleccionarDepto(pick);
    }
  };

  /* ---------------- validate / payload ---------------- */
  const validate = () => {
    if (!toIntOrZero(formValues.codloca)) return "No se pudo calcular el código de localidad.";
    if (!clean(formValues.localidad)) return "Ingresá la localidad.";
    if (!clean(formValues.provinciaId)) return "Seleccioná una provincia válida.";
    if (!clean(formValues.deptoId)) return "Seleccioná un depto válido.";
    return "";
  };

  const buildPayload = () => {
    return {
      codloca: toIntOrZero(formValues.codloca),
      localidad: clean(formValues.localidad),
      codpostal: clean(formValues.codpostal),
      codpais: 1,

      codprov: toIntOrZero(formValues.codprov),
      provincia: clean(formValues.provinciaId) || null,

      coddept: toIntOrZero(formValues.coddept),
      depto: clean(formValues.deptoId) || null,
    };
  };

  const resetForm = useCallback(
    (nextCod = "") => {
      setFormValues({
        ...initial,
        codloca: nextCod || getNextCodLoc(localidadesState.data),
      });

      setUi({
        provinciaDesc: "",
        deptoDesc: "",
      });
    },
    [getNextCodLoc, localidadesState.data]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validate();
    if (msg) return alert(msg);

    const payload = buildPayload();

    try {
      await addLocalidad(payload);

      const nuevoCod = Number(payload.codloca) || 0;
      const nextCod = String(nuevoCod + 1);

      setLocalidadesState((prev) => ({
        ...prev,
        data: [...prev.data, { codloca: nuevoCod }],
      }));

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        resetForm(nextCod);
        if (typeof setShow === "function") setShow(false);
      }, 900);
    } catch (error) {
      console.error("❌ Error al crear localidad:", error?.response?.data || error);
      alert("No se pudo guardar la localidad. Revisá consola / Network.");
    }
  };

  return (
    <>
      {!provinciasState.loading && !deptosState.loading && !localidadesState.loading && (
        <div className="container">
          <form onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group mt-3 col-sm-2">
                <label>Código</label>
                <input
                  type="number"
                  className="form-control"
                  name="codloca"
                  value={formValues.codloca}
                  readOnly
                  placeholder="Auto"
                />
              </div>

              <div className="form-group mt-3 col-sm-6">
                <label>Localidad</label>
                <input
                  type="text"
                  className="form-control"
                  name="localidad"
                  maxLength="80"
                  value={formValues.localidad}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Código Postal</label>
                <input
                  type="text"
                  className="form-control"
                  name="codpostal"
                  maxLength="10"
                  value={formValues.codpostal}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>País</label>
                <input className="form-control" value="ARGENTINA" readOnly />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-2">
                <label>Cod. Prov</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    name="codprov"
                    value={formValues.codprov}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        buscarProvincia(e.currentTarget.value);
                      }
                    }}
                    onBlur={(e) => {
                      const v = clean(e.currentTarget.value);
                      if (v === "0") buscarProvincia(v);
                      else if (v) fetchProvinciaByCod(v);
                    }}
                    min="0"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => buscarProvincia()}
                    title="Buscar provincia"
                    style={{
                      width: 44,
                      height: 38,
                      padding: 0,
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    🔎
                  </button>
                </div>
                <small className="text-muted">
                  <b>0</b> y Enter buscar
                </small>
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Provincia</label>
                <input
                  className="form-control"
                  value={ui.provinciaDesc}
                  readOnly
                  placeholder="Se completa desde catálogo"
                />
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Cod. Depto</label>
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    name="coddept"
                    value={formValues.coddept}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        buscarDepto(e.currentTarget.value);
                      }
                    }}
                    onBlur={(e) => {
                      const v = clean(e.currentTarget.value);
                      if (v === "0") buscarDepto(v);
                      else if (v) fetchDeptoByCod(v);
                    }}
                    min="0"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => buscarDepto()}
                    title="Buscar depto"
                    style={{
                      width: 44,
                      height: 38,
                      padding: 0,
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    🔎
                  </button>
                </div>
                <small className="text-muted">
                  <b>0</b> y Enter buscar
                </small>
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>Depto</label>
                <input
                  className="form-control"
                  value={ui.deptoDesc}
                  readOnly
                  placeholder="Se completa desde catálogo"
                />
              </div>
            </div>

            <button type="submit" className="btn btn-dark mt-5 mb-3" id="button">
              Guardar
            </button>
          </form>
        </div>
      )}

      <Modal show={showProvModal} onHide={closeProvModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Buscar provincia</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Código / Provincia</Form.Label>
            <Form.Control
              ref={inputProvRef}
              value={qProv}
              placeholder="Ej: tuc o 1"
              onChange={(e) => setQProv(e.target.value)}
              onKeyDown={onProvModalKeyDown}
            />
            <Form.Text className="text-muted">
              ↑ ↓ para moverte • Enter selecciona • Esc cierra
            </Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {!provOpts.length && (
              <div className="text-muted">
                {qProv.trim() ? "Sin resultados." : "Escribí para buscar o elegí de la lista."}
              </div>
            )}

            {!!provOpts.length && (
              <ListGroup>
                {provOpts.map((prov, idx) => {
                  const cod = getCodProv(prov);
                  const desc = getDescProv(prov);

                  return (
                    <ListGroup.Item
                      key={getProvinciaDocId(prov) || `${desc}-${idx}`}
                      action
                      active={idx === selIdxProv}
                      onClick={() => seleccionarProvincia(prov)}
                    >
                      {cod > 0 ? <b>{cod} — </b> : null}
                      {desc}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeProvModal}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={() => seleccionarProvincia(provOpts[selIdxProv] || provOpts[0])}
            disabled={!provOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeptoModal} onHide={closeDeptoModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Buscar depto</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Código / Depto</Form.Label>
            <Form.Control
              ref={inputDeptoRef}
              value={qDepto}
              placeholder="Ej: cap o 10"
              onChange={(e) => setQDepto(e.target.value)}
              onKeyDown={onDeptoModalKeyDown}
            />
            <Form.Text className="text-muted">
              ↑ ↓ para moverte • Enter selecciona • Esc cierra
            </Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {!deptoOpts.length && (
              <div className="text-muted">
                {qDepto.trim()
                  ? "Sin resultados."
                  : "Escribí para buscar o elegí de la lista."}
              </div>
            )}

            {!!deptoOpts.length && (
              <ListGroup>
                {deptoOpts.map((dep, idx) => {
                  const cod = getCodDept(dep);
                  const desc = getDescDept(dep);

                  return (
                    <ListGroup.Item
                      key={getDeptoDocId(dep) || `${desc}-${idx}`}
                      action
                      active={idx === selIdxDepto}
                      onClick={() => seleccionarDepto(dep)}
                    >
                      {cod > 0 ? <b>{cod} — </b> : null}
                      {desc}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeptoModal}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={() => seleccionarDepto(deptoOpts[selIdxDepto] || deptoOpts[0])}
            disabled={!deptoOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Registro generado correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
}